import { ft } from '../core/units.js';
import { penetrates } from '../core/collision.js';
import { EVENT } from './events.js';

/**
 * Getting out of sight.
 *
 * There is no list of hiding places in this file. There is a property —
 * `hollow` — that a thing in the data may have, meaning it has room under or
 * inside it that a person fits in. The dock crate does not have one. The stage
 * does. A round with a linen on it does, and a round without one does not,
 * which is the difference between a table and a tablecloth and not a rule about
 * banquets.
 *
 * What it does is exactly one thing: it stops light. A concealed player cannot
 * be seen and can be heard perfectly well, which is what makes a hiding place a
 * decision rather than a win button — the noise you made getting into it is
 * still on the bus, and a witness who already had a claim now has your last
 * known position and a reason to walk over.
 *
 * Getting in takes a moment and the moment is visible. You cannot dive under a
 * table in the frame somebody turns around.
 */

/** Seconds of holding the key. Long enough to be caught starting it. */
const ENTER_SECONDS = 0.8;

/** How far away a hollow can be and still be worth crouching towards. */
const REACH = ft(6);

/** Crouched. What has to fit in the gap. */
const CROUCH = 0.95;

export function createConcealment({ world, bus, hollows, tuning }) {
  let hidden = null;
  let entering = 0;
  let candidate = null;

  /**
   * Every hollow in reach, as spots.
   *
   * `hollows()` is supplied rather than computed, because what has a hollow
   * changes during a shift: a cart is parked somewhere different every few
   * minutes and a round only has one once somebody has thrown a linen over it.
   * This module does not know what any of them are.
   */
  function find(position) {
    let best = null;
    for (const hollow of hollows()) {
      const dx = hollow.x - position.x;
      const dz = hollow.z - position.z;
      const distance = Math.hypot(dx, dz);
      if (distance > REACH + (hollow.radius ?? 0)) continue;
      const gap = Math.max(0, distance - (hollow.radius ?? 0));
      if (best && gap >= best.gap) continue;
      best = { ...hollow, gap };
    }
    return best;
  }

  /** Whether a spot still has room for a crouched body. */
  function fits(spot) {
    return !penetrates(spot.x, spot.z, tuning.radius * 0.8, spot.y + 0.05, spot.y + CROUCH, world);
  }

  return {
    find,

    get hidden() {
      return hidden;
    },
    /** 0..1 while climbing in. The visible window. */
    get entering() {
      return hidden ? 1 : entering / ENTER_SECONDS;
    },
    get busy() {
      return Boolean(hidden) || entering > 0;
    },

    /**
     * One frame of trying to get in, or of being in.
     *
     * `wanted` is the key being held. Letting go part way through abandons it;
     * letting go once you are in gets you out, which is the same key doing the
     * same thing and needs no second binding.
     */
    update(dt, { position, wanted }) {
      if (hidden) {
        if (!wanted) {
          const was = hidden;
          hidden = null;
          entering = 0;
          bus?.emit(EVENT.NOISE, {
            actor: 'player', cause: 'rustle', position: { ...was }, radius: ft(10),
          });
          return { left: true, at: was };
        }
        if (!fits(hidden)) {
          // Whatever you were under has been moved or taken. You are standing
          // in the open and nobody had to tell you.
          const was = hidden;
          hidden = null;
          return { left: true, at: was, evicted: true };
        }
        return { in: hidden };
      }

      candidate = find(position);
      if (!wanted || !candidate || !fits(candidate)) {
        entering = 0;
        return { near: candidate };
      }

      entering += dt;
      if (entering < ENTER_SECONDS) return { near: candidate, entering: entering / ENTER_SECONDS };

      entering = 0;
      hidden = { x: candidate.x, y: candidate.y, z: candidate.z, label: candidate.label };
      bus?.emit(EVENT.NOISE, {
        actor: 'player', cause: 'rustle', position: { ...hidden }, radius: ft(10),
      });
      return { in: hidden };
    },

    clear() {
      hidden = null;
      entering = 0;
      candidate = null;
    },
  };
}
